import type { JSONContent } from "@tiptap/core";
import { z } from "zod";
import {
  chapterStatuses,
  sideStoryCanonStatuses,
  storyModes,
  type ChapterStatus,
  type StoryMode,
} from "@/types/story";

function isJsonDocument(value: unknown): value is JSONContent {
  return typeof value === "object" && value !== null && "type" in (value as Record<string, unknown>);
}

const jsonDocumentSchema = z.custom<JSONContent>(isJsonDocument, {
  message: "story.messages.saveError",
});

const storyModeSchema = z.enum(storyModes);
const chapterStatusSchema = z.enum(chapterStatuses);
const sideStoryCanonStatusSchema = z.enum(sideStoryCanonStatuses);

export const createMainStorySchema = z.object({
  body: jsonDocumentSchema.optional().default({
    type: "doc",
    content: [{ type: "paragraph" }],
  }),
  mode: storyModeSchema,
  summary: z.string().trim().max(240).optional().default(""),
  title: z.string().trim().min(1).max(160),
});

export const updateMainStorySchema = z.object({
  body: jsonDocumentSchema,
  summary: z.string().trim().max(240),
});

export const createChapterSchema = z.object({
  body: jsonDocumentSchema.optional().default({
    type: "doc",
    content: [{ type: "paragraph" }],
  }),
  imagePath: z.string().trim().min(1).nullable().optional(),
  status: chapterStatusSchema.optional().default("draft"),
  title: z.string().trim().min(1).max(160),
});

export const updateChapterSchema = z.object({
  body: jsonDocumentSchema,
  imagePath: z.string().trim().min(1).nullable().optional(),
  status: chapterStatusSchema,
  title: z.string().trim().min(1).max(160),
});

export const createSideStorySchema = z.object({
  body: jsonDocumentSchema.optional().default({
    type: "doc",
    content: [{ type: "paragraph" }],
  }),
  canonStatus: sideStoryCanonStatusSchema.optional().default("draft"),
  imagePaths: z.array(z.string().trim().min(1)).optional().default([]),
  summary: z.string().trim().min(1).max(240),
  title: z.string().trim().min(1).max(160),
});

export const updateSideStorySchema = z.object({
  body: jsonDocumentSchema,
  canonStatus: sideStoryCanonStatusSchema,
  imagePaths: z.array(z.string().trim().min(1)).optional().default([]),
  summary: z.string().trim().min(1).max(240),
  title: z.string().trim().min(1).max(160),
});

export type CreateMainStoryValues = z.output<typeof createMainStorySchema>;
export type UpdateMainStoryValues = z.output<typeof updateMainStorySchema>;
export type CreateChapterValues = z.output<typeof createChapterSchema>;
export type UpdateChapterValues = z.output<typeof updateChapterSchema>;
export type CreateSideStoryValues = z.output<typeof createSideStorySchema>;
export type UpdateSideStoryValues = z.output<typeof updateSideStorySchema>;
export type StoryModeInput = StoryMode;
export type ChapterStatusInput = ChapterStatus;
